import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router";
import { ExternalLink } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import {
  homePathFor,
  useHomePersona,
  type HomePersona,
} from "@shared-core/domain/homePersona";
import { useScopeOverrideValue } from "@shared-core/domain/scopeOverride";
import type * as API from "@shared-core/data/api-types";
import { HomePersonaToggle } from "../app/components/patterns/HomePersonaToggle";
import { ContributionTargetCard } from "../app/components/domain/pipeline/ContributionTargetCard";
import { PipelineHeroCard } from "../app/components/domain/pipeline/PipelineHeroCard";
import { QuicklinksRow } from "../app/components/domain/dashboard/QuicklinksRow";
import { ActivityMapSection } from "../app/components/domain/dashboard/ActivityMapSection";
import { DashboardToDoSection } from "../app/components/domain/dashboard/DashboardToDoSection";
import { AlertsMetricCard } from "../app/components/domain/alerts/AlertsMetricCard";
import { RecommendationsMetricCard } from "../app/components/domain/recommendations/RecommendationsMetricCard";
import { AccountAllocationMetricCard } from "../app/components/domain/account-allocation/AccountAllocationMetricCard";
import { AwaitingInvoiceMetricCard } from "../app/components/domain/dashboard/AwaitingInvoiceMetricCard";
import { paths, isRouteEnabled } from "@shared-core/routing/routes";
import { useAuth, useCanManageAccountAllocation } from "../app/auth/hooks";

// Literal class names so Tailwind's scanner sees each one.
const METRIC_GRID_COLS: Record<number, string> = {
  2: "md:grid-cols-2",
  3: "md:grid-cols-3",
  4: "md:grid-cols-2 lg:grid-cols-4",
};

export function SalesHome() {
  const dataAdapter = useDataAdapter();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManageAccountAllocation = useCanManageAccountAllocation();
  const { setShowAll } = useScopeOverrideValue({ role: user?.role });
  const [unallocatedTotal, setUnallocatedTotal] = useState<number | null>(null);
  useEffect(() => {
    if (!canManageAccountAllocation || !isRouteEnabled("accountAllocation")) {
      return;
    }
    let cancelled = false;
    dataAdapter.getAllocationSummary().then((summary) => {
      if (!cancelled) setUnallocatedTotal(summary.unallocated_total);
    });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, canManageAccountAllocation]);
  // Role is only used for the default when no preference is stored; the
  // on-page toggle always sets an explicit persona, so it isn't needed here.
  const { setPersona } = useHomePersona({ role: undefined });
  const switchPersona = (next: HomePersona) => {
    setPersona(next);
    navigate(homePathFor(next));
  };
  const [data, setData] = useState<API.DashboardHome | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const result = await dataAdapter.getDashboardHome();
        setData(result);
      } catch (error) {
        console.error("Error loading dashboard data:", error);
      } finally {
        setLoading(false);
      }
    }

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only dashboard fetch; adapter identity is stable in-app
  }, []);

  const showAwaitingInvoice = isRouteEnabled("jobs");
  const showAccountAllocation =
    canManageAccountAllocation && unallocatedTotal != null;
  const metricTileCount =
    2 + (showAwaitingInvoice ? 1 : 0) + (showAccountAllocation ? 1 : 0);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between mb-1 gap-4 flex-wrap">
          <h1 className="text-2xl">My customer portfolio</h1>
          <div className="flex items-center gap-3">
            <HomePersonaToggle current="sales" onSelect={switchPersona} />
            <Link
              to={paths.reports()}
              className="text-sm text-primary hover:underline inline-flex items-center gap-1"
            >
              Sales report
              <ExternalLink className="size-3" />
            </Link>
            <Link
              to={paths.customers()}
              onClick={() => setShowAll(false)}
              className="text-sm text-primary hover:underline inline-flex items-center gap-1"
            >
              View all my customers
              <ExternalLink className="size-3" />
            </Link>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          My contribution targets, activity, to-do list and quick access to the
          rest of One
        </p>
      </div>

      {/* Top strip: Contribution + Pipeline, kept short so the sections below
          (quicklinks, to-do list, where things stand) are visible without
          scrolling. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 [&_[data-slot=card]]:shadow-md [&_[data-slot=card]]:border-gray-300">
        <ContributionTargetCard />
        <PipelineHeroCard pipeline={data?.pipeline} />
      </div>

      <QuicklinksRow />

      <div className="border-t border-border pt-8">
        <DashboardToDoSection />
      </div>

      <div className="border-t border-border pt-8">
        <ActivityMapSection />
      </div>

      {/* Alerts/Opportunities keep their own full pages (triage, filters,
          convert-to-ticket) — these are just the "how do I get there, and
          how many are waiting" entry points now that the to-do list above
          consolidates already-converted tickets rather than raw
          alerts/opportunities. Awaiting invoice is the same kind of entry
          point onto the rep's own jobs list. */}
      <div
        className={`border-t border-border pt-8 grid grid-cols-1 gap-4 [&_[data-slot=card]]:shadow-md [&_[data-slot=card]]:border-gray-300 ${
          METRIC_GRID_COLS[metricTileCount]
        }`}
      >
        <AlertsMetricCard
          criticalCount={data.alert_severity_counts?.critical ?? 0}
          highCount={data.alert_severity_counts?.high ?? 0}
        />
        <RecommendationsMetricCard
          urgentCount={data.opportunity_priority_counts?.urgent ?? 0}
          recommendedCount={data.opportunity_priority_counts?.recommended ?? 0}
        />
        {showAwaitingInvoice && <AwaitingInvoiceMetricCard />}
        {showAccountAllocation && (
          <AccountAllocationMetricCard unallocatedTotal={unallocatedTotal} />
        )}
      </div>
    </div>
  );
}
